// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { WheelPickerOption, WheelPickerProps } from 'util/Types';
import { useControllableState } from 'util/hooks/useControllableState';
import styles from './WheelPicker.module.scss';

const DEFAULT_RESISTANCE = 0.3;
const DEFAULT_MAX_VELOCITY = 30;
const DEFAULT_VISIBLE_COUNT = 20;
const DEFAULT_ITEM_HEIGHT = 58;
const DEFAULT_DRAG_SENSITIVITY = 3;
const DEFAULT_NUMBER_OF_VISIBLE_ITEMS = 3;

const easeOutCubic = (p: number) => Math.pow(p - 1, 3) + 1;

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(value, max));

export const WheelPicker: React.FC<WheelPickerProps> = ({
  options: optionsProp,
  defaultValue,
  value: valueProp,
  onValueChange,
  infinite: infiniteProp = true,
  className,
  configs,
}) => {
  const [value = optionsProp[0]?.value ?? '', setValue] = useControllableState({
    defaultProp: defaultValue,
    prop: valueProp,
    onChange: onValueChange,
  });

  const {
    resistance,
    maxVelocity,
    visibleCount,
    itemHeight,
    dragSensitivity,
    numberOfVisibleItems,
  } = useMemo(
    () => ({
      resistance: configs?.resistance ?? DEFAULT_RESISTANCE,
      maxVelocity: configs?.maxVelocity ?? DEFAULT_MAX_VELOCITY,
      visibleCount: configs?.visibleCount ?? DEFAULT_VISIBLE_COUNT,
      itemHeight: configs?.itemHeight ?? DEFAULT_ITEM_HEIGHT,
      dragSensitivity: configs?.dragSensitivity ?? DEFAULT_DRAG_SENSITIVITY,
      numberOfVisibleItems:
        configs?.numberOfVisibleItems ?? DEFAULT_NUMBER_OF_VISIBLE_ITEMS,
    }),
    [configs]
  );

  // Create options with infinite loop support
  const options = useMemo<WheelPickerOption[]>(() => {
    if (!infiniteProp) {
      return optionsProp;
    }

    const result: WheelPickerOption[] = [];
    const halfCount = Math.ceil(visibleCount / 2);

    if (optionsProp.length === 0) {
      return result;
    }

    while (result.length < halfCount) {
      result.push(...optionsProp);
    }

    return result;
  }, [visibleCount, optionsProp, infiniteProp]);

  // Calculations
  const containerHeight = itemHeight * numberOfVisibleItems;
  const quarterCount = visibleCount >> 2;
  const baseDeceleration = dragSensitivity * 10;
  const snapBackDeceleration = 10;

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const wheelItemsRef = useRef<HTMLUListElement>(null);
  const highlightListRef = useRef<HTMLUListElement>(null);
  const scrollRef = useRef(0);
  const moveId = useRef(0);
  const dragingRef = useRef(false);
  const lastWheelRef = useRef(0);
  const dragControllerRef = useRef<AbortController | null>(null);

  const touchDataRef = useRef<{
    startY: number;
    yList: [number, number][];
    touchScroll?: number;
  }>({
    startY: 0,
    yList: [],
  });

  // Render wheel items
  const renderWheelItems = useMemo(() => {
    const renderItem = (item: WheelPickerOption, index: number) => (
      <li
        key={index}
        data-index={index}
        style={{
          position: 'absolute',
          top: index * itemHeight,
          left: 0,
          width: '100%',
          height: itemHeight,
          lineHeight: `${itemHeight}px`,
          visibility: 'visible',
          fontSize: '36px',
          fontWeight: 700,
          WebkitFontSmoothing: 'subpixel-antialiased',
        }}
      >
        {item.label}
      </li>
    );

    const items = options.map((option, index) => renderItem(option, index));

    if (infiniteProp) {
      for (let i = 0; i < quarterCount; ++i) {
        const prependIndex = -i - 1;
        const appendIndex = i + options.length;

        items.unshift(
          renderItem(options[options.length - i - 1], prependIndex)
        );
        items.push(renderItem(options[i], appendIndex));
      }
    }

    return items;
  }, [infiniteProp, options, quarterCount, itemHeight]);

  // Render highlight items
  const renderHighlightItems = useMemo(() => {
    const renderItem = (item: WheelPickerOption, key: React.Key) => (
      <li
        key={key}
        style={{
          height: itemHeight,
          lineHeight: `${itemHeight}px`,
          fontSize: '36px',
          fontWeight: 700,
        }}
      >
        {item.label}
      </li>
    );

    const items = options.map((option, index) => renderItem(option, index));

    if (infiniteProp) {
      const firstItem = options[0];
      const lastItem = options[options.length - 1];

      items.unshift(renderItem(lastItem, 'infinite-start'));
      items.push(renderItem(firstItem, 'infinite-end'));
    }

    return items;
  }, [infiniteProp, options]);

  // Scroll utilities
  const normalizeScroll = (scroll: number) =>
    ((scroll % options.length) + options.length) % options.length;

  const scrollTo = (scroll: number) => {
    const normalizedScroll = infiniteProp ? normalizeScroll(scroll) : scroll;

    if (wheelItemsRef.current) {
      const transform = `translateY(calc(${
        -normalizedScroll * itemHeight - itemHeight / 2
      }px))`;
      wheelItemsRef.current.style.transform = transform;

      wheelItemsRef.current.childNodes.forEach((node) => {
        const li = node as HTMLLIElement;
        const distance = Math.abs(Number(li.dataset.index) - normalizedScroll);
        li.style.visibility = distance > quarterCount ? 'hidden' : 'visible';
      });
    }

    if (highlightListRef.current) {
      highlightListRef.current.style.transform = `translateY(${
        -normalizedScroll * itemHeight
      }px)`;
    }

    return normalizedScroll;
  };

  // Animation utilities
  const cancelAnimation = () => {
    cancelAnimationFrame(moveId.current);
  };

  const animateScroll = (
    startScroll: number,
    endScroll: number,
    duration: number,
    onComplete?: () => void
  ) => {
    if (startScroll === endScroll || duration === 0) {
      scrollTo(startScroll);
      return;
    }

    const startTime = performance.now();
    const totalDistance = endScroll - startScroll;

    const tick = (currentTime: number) => {
      const elapsed = (currentTime - startTime) / 1000;

      if (elapsed < duration) {
        const progress = easeOutCubic(elapsed / duration);
        scrollRef.current = scrollTo(startScroll + progress * totalDistance);
        moveId.current = requestAnimationFrame(tick);
      } else {
        cancelAnimation();
        scrollRef.current = scrollTo(endScroll);
        onComplete?.();
      }
    };

    requestAnimationFrame(tick);
  };

  // Selection utilities
  const selectByScroll = (scroll: number) => {
    const normalized = normalizeScroll(scroll) | 0;
    const boundedScroll = infiniteProp
      ? normalized
      : Math.min(Math.max(normalized, 0), options.length - 1);

    if (!infiniteProp && boundedScroll !== scroll) return;

    scrollRef.current = scrollTo(boundedScroll);
    const selected = options[scrollRef.current];
    setValue(selected.value);
  };

  const selectByValue = (value: string) => {
    const index = options.findIndex((opt) => opt.value === value);

    if (index === -1) {
      return;
    }

    cancelAnimation();
    selectByScroll(index);
  };

  // Drag handling
  const updateScrollDuringDrag = (e: MouseEvent | TouchEvent) => {
    try {
      const currentY =
        (e instanceof MouseEvent ? e.clientY : e.touches?.[0]?.clientY) || 0;

      const touchData = touchDataRef.current;
      touchData.yList.push([currentY, Date.now()]);
      if (touchData.yList.length > 5) {
        touchData.yList.shift();
      }

      const dragDelta = (touchData.startY - currentY) / itemHeight;
      let nextScroll = scrollRef.current + dragDelta;

      if (infiniteProp) {
        nextScroll = normalizeScroll(nextScroll);
      } else {
        const maxIndex = options.length;
        if (nextScroll < 0) {
          nextScroll *= resistance;
        } else if (nextScroll > maxIndex) {
          nextScroll = maxIndex + (nextScroll - maxIndex) * resistance;
        }
      }

      touchData.touchScroll = scrollTo(nextScroll);
      // eslint-disable-next-line no-empty
    } catch (_) {}
  };

  const initiateDragGesture = (event: MouseEvent | TouchEvent) => {
    try {
      dragingRef.current = true;

      const controller = new AbortController();
      const { signal } = controller;
      dragControllerRef.current = controller;

      const passiveOpts = { signal, passive: false };
      containerRef.current?.addEventListener(
        'touchmove',
        handleDragMoveEvent,
        passiveOpts
      );
      document.addEventListener('mousemove', handleDragMoveEvent, passiveOpts);

      const startY =
        (event instanceof MouseEvent
          ? event.clientY
          : event.touches?.[0]?.clientY) || 0;

      const touchData = touchDataRef.current;
      touchData.startY = startY;
      touchData.yList = [[startY, Date.now()]];
      touchData.touchScroll = scrollRef.current;

      cancelAnimation();
      // eslint-disable-next-line no-empty
    } catch (_) {}
  };

  const handleDragMoveEvent = (event: MouseEvent | TouchEvent) => {
    if (
      !dragingRef.current &&
      !containerRef.current?.contains(event.target as Node) &&
      event.target !== containerRef.current
    ) {
      return;
    }

    if (event.cancelable) {
      event.preventDefault();
    }

    if (options.length) {
      updateScrollDuringDrag(event);
    }
  };

  const handleDragStartEvent = useCallback(
    (e: MouseEvent | TouchEvent) => {
      const isDragging = dragingRef.current;
      const isTargetValid =
        containerRef.current?.contains(e.target as Node) ||
        e.target === containerRef.current;

      if ((isDragging || isTargetValid) && e.cancelable) {
        e.preventDefault();
        if (options.length) {
          initiateDragGesture(e);
        }
      }
    },
    [initiateDragGesture]
  );

  // Inertia scrolling
  const decelerateAndAnimateScroll = (initialVelocity: number) => {
    const currentScroll = scrollRef.current;
    let targetScroll = currentScroll;
    let deceleration =
      initialVelocity > 0 ? -baseDeceleration : baseDeceleration;
    let duration = 0;

    if (infiniteProp) {
      duration = Math.abs(initialVelocity / deceleration);
      const scrollDistance =
        initialVelocity * duration + 0.5 * deceleration * duration * duration;
      targetScroll = Math.round(currentScroll + scrollDistance);
    } else if (currentScroll < 0 || currentScroll > options.length - 1) {
      const target = clamp(currentScroll, 0, options.length - 1);
      const scrollDistance = currentScroll - target;
      deceleration = snapBackDeceleration;
      duration = Math.sqrt(Math.abs(scrollDistance / deceleration));
      targetScroll = target;
    } else {
      duration = Math.abs(initialVelocity / deceleration);
      const scrollDistance =
        initialVelocity * duration + 0.5 * deceleration * duration * duration;
      targetScroll = Math.round(currentScroll + scrollDistance);
      targetScroll = clamp(targetScroll, 0, options.length - 1);

      const adjustedDistance = targetScroll - currentScroll;
      duration = Math.sqrt(Math.abs(adjustedDistance / deceleration));
    }

    animateScroll(currentScroll, targetScroll, duration, () => {
      selectByScroll(scrollRef.current);
    });

    selectByScroll(scrollRef.current);
  };

  const finalizeDragAndStartInertiaScroll = () => {
    try {
      dragControllerRef.current?.abort();
      dragControllerRef.current = null;

      const touchData = touchDataRef.current;
      const yList = touchData.yList;
      let velocity = 0;

      if (yList.length > 1) {
        const len = yList.length;
        const [startY, startTime] = yList[len - 2] ?? [0, 0];
        const [endY, endTime] = yList[len - 1] ?? [0, 0];

        const timeDiff = endTime - startTime;

        if (timeDiff > 0) {
          const distance = startY - endY;
          const velocityPerSecond = ((distance / itemHeight) * 1000) / timeDiff;

          const direction = velocityPerSecond > 0 ? 1 : -1;
          const absVelocity = Math.min(
            Math.abs(velocityPerSecond),
            maxVelocity
          );
          velocity = absVelocity * direction;
        }
      }

      scrollRef.current = touchData.touchScroll ?? scrollRef.current;
      decelerateAndAnimateScroll(velocity);
      // eslint-disable-next-line no-empty
    } catch (_) {
    } finally {
      dragingRef.current = false;
    }
  };

  const handleDragEndEvent = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!options.length) return;

      const isDragging = dragingRef.current;
      const isTargetValid =
        containerRef.current?.contains(event.target as Node) ||
        event.target === containerRef.current;

      if ((isDragging || isTargetValid) && event.cancelable) {
        event.preventDefault();
        finalizeDragAndStartInertiaScroll();
      }
    },
    [finalizeDragAndStartInertiaScroll]
  );

  // Wheel scrolling
  const scrollByWheel = (event: WheelEvent) => {
    event.preventDefault();

    const now = Date.now();
    if (now - lastWheelRef.current < 100) return;

    const direction = Math.sign(event.deltaY);
    if (!direction) return;

    lastWheelRef.current = now;

    const startScroll = scrollRef.current;
    let endScroll = startScroll + direction;

    if (infiniteProp) {
      endScroll = Math.round(endScroll);
    } else {
      endScroll = clamp(Math.round(endScroll), 0, options.length - 1);
    }

    const distance = Math.abs(endScroll - startScroll);
    if (distance === 0) return;

    const duration = Math.sqrt(distance / 5);

    cancelAnimation();
    animateScroll(startScroll, endScroll, duration, () => {
      selectByScroll(scrollRef.current);
    });
  };

  const handleWheelEvent = useCallback(
    (event: WheelEvent) => {
      if (!options.length || !containerRef.current) return;

      const isDragging = dragingRef.current;
      const isTargetValid =
        containerRef.current.contains(event.target as Node) ||
        event.target === containerRef.current;

      if ((isDragging || isTargetValid) && event.cancelable) {
        event.preventDefault();
        scrollByWheel(event);
      }
    },
    [scrollByWheel]
  );

  // Event listeners
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const controller = new AbortController();
    const { signal } = controller;

    const opts = { signal, passive: false };

    container.addEventListener('touchstart', handleDragStartEvent, opts);
    container.addEventListener('touchend', handleDragEndEvent, opts);
    container.addEventListener('wheel', handleWheelEvent, opts);
    document.addEventListener('mousedown', handleDragStartEvent, opts);
    document.addEventListener('mouseup', handleDragEndEvent, opts);

    return () => controller.abort();
  }, [handleDragEndEvent, handleDragStartEvent, handleWheelEvent]);

  // Update selection when value changes
  useEffect(() => {
    selectByValue(value);
  }, [value, valueProp]);

  useEffect(() => {
    scrollTo(Number(defaultValue) || 0);
  }, []);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        position: 'relative',
        flex: 1,
        overflow: 'hidden',
        cursor: 'ns-resize',
        textAlign: 'center',
        height: containerHeight,
      }}
    >
      <ul
        ref={wheelItemsRef}
        style={{
          display: 'block',
          position: 'absolute',
          top: '50%',
          left: 0,
          width: '100%',
          height: 0,
          marginLeft: 'auto',
          marginRight: 'auto',
          WebkitFontSmoothing: 'subpixel-antialiased',
          willChange: 'transform',
          listStyle: 'none',
          padding: 0,
          margin: 0,
        }}
      >
        {renderWheelItems}
      </ul>

      <div
        style={{
          position: 'absolute',
          top: '50%',
          width: '100%',
          fontSize: '36px',
          fontWeight: 700,
          transform: 'translateY(-50%)',
          overflow: 'hidden',
          height: itemHeight,
          lineHeight: itemHeight + 'px',
        }}
        className={styles.highlightWrapper}
      >
        <ul
          ref={highlightListRef}
          style={{
            position: 'absolute',
            width: '100%',
            listStyle: 'none',
            padding: 0,
            margin: 0,
            top: infiniteProp ? -itemHeight : undefined,
          }}
        >
          {renderHighlightItems}
        </ul>
      </div>
    </div>
  );
};
